import React, { useEffect, useRef } from 'react';
import { useTheme } from '@mui/material/styles';
import { useGoogleMaps } from '../../../../utils/maps/GoogleMapsProvider';

export interface WeightedMapPoint {
  location: { lat: number; lng: number };
  weight: number;
}

interface WeightedPointsMapProps {
  points: WeightedMapPoint[];
  height?: string;
}

const DEFAULT_CENTER = { lat: 39.8283, lng: -98.5795 };
const DEFAULT_ZOOM = 4;

const WeightedPointsMap: React.FC<WeightedPointsMapProps> = ({ points, height = '200px' }) => {
  const theme = useTheme();
  const { loaded } = useGoogleMaps();
  const mapContainerRef = useRef<HTMLDivElement | null>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const circlesRef = useRef<google.maps.Circle[]>([]);

  useEffect(() => {
    if (!loaded || !mapContainerRef.current) {
      return;
    }

    if (!mapRef.current) {
      mapRef.current = new window.google.maps.Map(mapContainerRef.current, {
        center: DEFAULT_CENTER,
        zoom: DEFAULT_ZOOM,
        disableDefaultUI: true,
        streetViewControl: false,
        mapTypeControl: false,
        zoomControl: false,
        fullscreenControl: true,
      });
    }

    circlesRef.current.forEach((circle) => circle.setMap(null));
    circlesRef.current = [];

    if (points.length === 0) {
      mapRef.current.setCenter(DEFAULT_CENTER);
      mapRef.current.setZoom(DEFAULT_ZOOM);
      return;
    }

    const maxWeight = Math.max(...points.map((point) => point.weight), 1);

    points.forEach(({ location, weight }) => {
      const normalizedWeight = weight / maxWeight;
      const circle = new google.maps.Circle({
        map: mapRef.current!,
        center: location,
        radius: 20000 + normalizedWeight * 80000,
        strokeWeight: 0,
        fillColor: '#EA4335',
        fillOpacity: 0.15 + normalizedWeight * 0.45,
      });
      circlesRef.current.push(circle);
    });

    const bounds = new google.maps.LatLngBounds();
    points.forEach((point) => bounds.extend(point.location));
    mapRef.current.fitBounds(bounds);
  }, [loaded, points]);

  if (!loaded) {
    return null;
  }

  return (
    <div
      ref={mapContainerRef}
      style={{
        width: '100%',
        height,
        color: theme.palette.text.primary,
        fontSize: theme.typography.textSM.fontSize,
      }}
    />
  );
};

export default WeightedPointsMap;
